// src/app/services/laboratorio/laboratorio-parser.service.ts

import { Injectable } from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';
import { ParametroLaboratorio } from '../../interfaces/laboratorio/parametro-laboratorio.interface';
import { ResultadoExtraido, NivelAlerta } from '../../interfaces/laboratorio/resultado-laboratorio.interface';

@Injectable({
  providedIn: 'root',
})
export class LaboratorioParserService {
  private parametrosCatalogo: ParametroLaboratorio[] = [];

  constructor() {
    // Configurar worker de PDF.js
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  }

  /**
   * Cargar catálogo de parámetros
   */
  setParametrosCatalogo(parametros: ParametroLaboratorio[]) {
    this.parametrosCatalogo = parametros;
    console.log(`📋 Catálogo cargado: ${parametros.length} parámetros`);
  }

  /**
   * Extraer texto completo del PDF
   */
  async extractTextFromPDF(file: File): Promise<string> {
    console.log('📄 Iniciando extracción de PDF...');
    const startTime = performance.now();

    try {
      // 1. Convertir File a ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();

      // 2. Cargar PDF con PDF.js
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;

      console.log(`📄 PDF cargado: ${pdf.numPages} páginas`);

      // 3. Extraer texto de TODAS las páginas
      let fullText = '';

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();

        // Concatenar todo el texto de la página
        const pageText = textContent.items.map((item: any) => item.str).join(' ');

        fullText += pageText + '\n\n';

        console.log(`📄 Página ${pageNum}/${pdf.numPages} procesada`);
      }

      const endTime = performance.now();
      console.log(`✅ Extracción completada en ${(endTime - startTime).toFixed(0)}ms`);
      console.log(`📝 Texto extraído: ${fullText.length} caracteres`);

      return fullText;
    } catch (error) {
      console.error('❌ Error extrayendo texto del PDF:', error);
      throw new Error('No se pudo extraer el texto del PDF. Verifica que sea un PDF válido.');
    }
  }

  /**
   * Extraer parámetros del texto usando REGEX
   */
  matchParameters(texto: string, categoria?: string): ResultadoExtraido[] {
    console.log('🔍 Buscando parámetros en texto...');

    const resultados: ResultadoExtraido[] = [];

    // Filtrar parámetros por categoría si se especifica
    const parametrosFiltrados = categoria
      ? this.parametrosCatalogo.filter((p) => p.categoria === categoria)
      : this.parametrosCatalogo;

    console.log(`🔍 Buscando ${parametrosFiltrados.length} parámetros...`);

    for (const parametro of parametrosFiltrados) {
      // Crear regex dinámico para cada parámetro
      const pattern = this.buildRegex(parametro);
      const match = texto.match(pattern);

      if (match) {
        const valor = this.extraerValor(match, parametro);
        const confianza = this.calcularConfianza(match, parametro, valor);
        const analisis = this.analizarValor(valor, parametro);

        resultados.push({
          parametroId: parametro.id,
          parametroNombre: parametro.nombre,
          valor: valor,
          unidad: parametro.unidad,
          textoOriginal: match[0],
          confianza: confianza,
          fueraRango: analisis.fueraRango,
          nivelAlerta: analisis.nivel,
        });

        console.log(
          `✓ ${parametro.nombre} = ${valor} ${parametro.unidad} (${(confianza * 100).toFixed(0)}% confianza)`,
        );
      }
    }

    console.log(`✅ ${resultados.length} parámetros extraídos`);
    return resultados;
  }

  /**
   * Construir regex específico para cada parámetro
   */
  private buildRegex(parametro: ParametroLaboratorio): RegExp {
    // Unir todos los aliases: "Hb|HGB|Hemoglobina"
    const aliases = parametro.aliases.map((alias) => this.escapeRegex(alias)).join('|');

    if (parametro.tipo === 'numerico') {
      // Patrón: "Hemoglobina: 14.5 g/dL" o "Hb 14.5"
      // Permite espacios, dos puntos opcionales, números decimales con punto o coma
      return new RegExp(
        `(${aliases})\\s*:?\\s*([0-9]+[.,]?[0-9]*)\\s*(${this.escapeRegex(parametro.unidad || '')})?`,
        'i', // Case insensitive
      );
    } else if (parametro.tipo === 'cualitativo') {
      // Patrón: "Glucosa: Negativo" o "Proteínas: ++"
      const valores = [...(parametro.valoresNormales || []), ...(parametro.valoresAnormales || [])]
        .map((v) => this.escapeRegex(v))
        .join('|');

      return new RegExp(`(${aliases})\\s*:?\\s*(${valores})`, 'i');
    } else {
      // Texto libre
      return new RegExp(`(${aliases})\\s*:?\\s*([A-Za-zÀ-ÿ\\s]+)`, 'i');
    }
  }

  /**
   * Escapar caracteres especiales de regex
   */
  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Extraer el valor numérico o texto del match
   */
  private extraerValor(match: RegExpMatchArray, parametro: ParametroLaboratorio): number | string {
    const valorStr = match[2].trim();

    if (parametro.tipo === 'numerico') {
      // Normalizar: reemplazar coma por punto
      return parseFloat(valorStr.replace(',', '.'));
    }

    return valorStr;
  }

  /**
   * Analizar si el valor está fuera de rango
   */
  private analizarValor(
    valor: number | string,
    parametro: ParametroLaboratorio,
  ): { fueraRango: boolean; nivel: NivelAlerta } {
    if (parametro.tipo !== 'numerico' || typeof valor !== 'number') {
      return { fueraRango: false, nivel: 'normal' };
    }

    const rangoMin = parametro.rangoMin;
    const rangoMax = parametro.rangoMax;

    if (rangoMin !== undefined && valor < rangoMin) {
      return { fueraRango: true, nivel: 'bajo' };
    }

    if (rangoMax !== undefined && valor > rangoMax) {
      return { fueraRango: true, nivel: 'alto' };
    }

    return { fueraRango: false, nivel: 'normal' };
  }

  /**
   * Calcular nivel de confianza del match (0.0 - 1.0)
   */
  private calcularConfianza(match: RegExpMatchArray, parametro: ParametroLaboratorio, valor: number | string): number {
    let confianza = 0.5; // Base

    // +0.2 si el nombre coincide exactamente (no es alias)
    if (match[1].toLowerCase() === parametro.nombre.toLowerCase()) {
      confianza += 0.2;
    }

    // +0.15 si la unidad coincide
    if (match[3] && match[3].toLowerCase() === parametro.unidad?.toLowerCase()) {
      confianza += 0.15;
    }

    // +0.15 si el valor es numérico y válido
    if (parametro.tipo === 'numerico' && typeof valor === 'number' && !isNaN(valor)) {
      confianza += 0.15;

      // +0.1 adicional si el valor está dentro de un rango razonable (no extremo)
      if (parametro.rangoMin !== undefined && parametro.rangoMax !== undefined) {
        const rangoExtendido = (parametro.rangoMax - parametro.rangoMin) * 3;
        if (valor >= parametro.rangoMin - rangoExtendido && valor <= parametro.rangoMax + rangoExtendido) {
          confianza += 0.1;
        }
      }
    }

    return Math.min(confianza, 1.0);
  }
}
